import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { WarningBanner, JobProcessingBanner } from '../components/ui/warning-banner';
import { 
  Activity,
  Plus,
  Eye,
  TrendingUp,
  CheckCircle,
  Package2,
  Zap,
  UserCheck,
  Loader2,
  History,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  PlayCircle,
  CheckSquare,
  XCircle,
  Pause,
  RefreshCw
} from 'lucide-react';
import { JobsList } from '../components/jobs/JobsList';
import { Job, jobService } from '../services/job';
import { useBulkSelection } from '../contexts/BulkSelectionContext';
import { jobHistoryManager, JobHistoryEntry } from '../utils/jobHistory';
import { toast } from '../hooks/use-toast';
import { useJobPolling } from '../hooks/use-job-polling';
import { useOrganization } from '../contexts/OrganizationContext';

export default function JobsPage() {
  const navigate = useNavigate();
  const { selection } = useBulkSelection();
  const { hasPermission } = useOrganization();
  
  // Job statistics
  const [jobStats, setJobStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    processed_pending_approval: 0,
    approved: 0,
    uploading_to_magento: 0,
    complete: 0,
    failed: 0,
    rejected: 0,
    cancelled: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  
  // Job filtering
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
  
  // Job history
  const [jobHistory, setJobHistory] = useState<JobHistoryEntry[]>([]);
  const [isJobHistoryExpanded, setIsJobHistoryExpanded] = useState(false);
  
  // Active async job monitoring
  const [activeAsyncJob, setActiveAsyncJob] = useState<JobHistoryEntry | null>(null);
  
  // Refresh key to force JobsList reload
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  
  // Individual job updates for smart polling
  const [jobUpdates, setJobUpdates] = useState<Record<string, string>>({});

  // Load job statistics
  const loadJobStats = async () => {
    try {
      setLoadingStats(true);
      const response = await jobService.getJobStats();
      if (response.success) {
        setJobStats(response.data);
        setLastRefresh(Date.now());
      }
    } catch (error) {
      console.error('Failed to load job statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Load job history and check for active jobs
  const loadJobHistory = () => {
    const history = jobHistoryManager.getJobHistory();
    setJobHistory(history);
    
    // Check for active async job
    const currentJobId = jobHistoryManager.getCurrentJobId();
    if (currentJobId) {
      const currentJob = jobHistoryManager.getJob(currentJobId);
      if (currentJob && currentJob.status === 'creating') {
        setActiveAsyncJob(currentJob);
        setIsJobHistoryExpanded(true);
      }
    }
  };

  // Handle individual job updates from polling
  const handleJobUpdate = useCallback((jobId: string, oldStatus: string, newStatus: string) => {
    // Update job updates map
    setJobUpdates(prev => ({ ...prev, [jobId]: newStatus }));
    
    // Update stats based on status change
    setJobStats(prev => {
      const updated = { ...prev };
      
      // Decrement old status count
      if (oldStatus in updated && updated[oldStatus as keyof typeof updated] > 0) {
        (updated as any)[oldStatus]--;
      }
      
      // Increment new status count
      if (newStatus in updated) {
        (updated as any)[newStatus]++;
      }
      
      return updated;
    });
    
    // Update the job in the JobsList component
    if ((window as any).__updateJobStatus) {
      (window as any).__updateJobStatus(jobId, newStatus);
    }
    
    setLastRefresh(Date.now());
  }, []);
  
  // Smart background polling for active jobs only
  const { activeJobs, isPolling } = useJobPolling({
    enabled: autoRefresh,
    interval: 5000,
    onJobUpdate: handleJobUpdate,
    onStatsUpdate: loadJobStats
  });

  useEffect(() => {
    loadJobStats();
    loadJobHistory();

    // Listen for stats updates from approval page
    const handleJobStatsUpdate = () => {
      loadJobStats();
    };

    window.addEventListener('jobStatsUpdate', handleJobStatsUpdate);
    
    return () => {
      window.removeEventListener('jobStatsUpdate', handleJobStatsUpdate);
    };
  }, []);

  const handleCreateJobs = () => {
    if (selection.selectedIds.size === 0) {
      toast({
        title: 'No Products Selected',
        description: 'Please select products from the Products page first.',
        variant: 'destructive',
      });
      navigate('/products');
      return;
    }
    navigate('/jobs/create');
  };

  const handleJobSelect = (job: Job) => {
    navigate(`/jobs/details?job_id=${job.id}`);
  };

  const handleMonitorAsyncJob = (jobId: string) => {
    navigate(`/jobs/async-monitor?job_id=${jobId}`);
  };

  const handleViewPendingApprovals = () => {
    navigate('/jobs/approval');
  };

  const handleViewProcessingJobs = () => {
    navigate('/jobs/monitor');
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };

  const getStatCardConfig = (status: string) => {
    const isSelected = selectedStatusFilter === status;
    
    switch (status) {
      case 'total': 
        return { 
          icon: <Package2 className="h-5 w-5" />, 
          color: 'text-muted-foreground', 
          bg: isSelected ? 'bg-muted/80 border-muted-foreground' : 'bg-muted/50 border-border',
          iconBg: 'bg-muted/70',
          label: 'Total Jobs',
          clickable: false
        };
      case 'pending': 
        return { 
          icon: <Clock className="h-5 w-5" />, 
          color: 'text-yellow-600 dark:text-yellow-400', 
          bg: isSelected ? 'bg-muted/80 border-yellow-500 dark:border-yellow-600' : 'bg-card border-yellow-200 dark:border-yellow-800',
          iconBg: 'bg-yellow-100 dark:bg-yellow-950/50',
          label: 'Pending',
          clickable: true
        };
      case 'processing': 
        return { 
          icon: <PlayCircle className="h-5 w-5" />, 
          color: 'text-blue-600 dark:text-blue-400', 
          bg: isSelected ? 'bg-muted/80 border-blue-500 dark:border-blue-600' : 'bg-card border-blue-200 dark:border-blue-800',
          iconBg: 'bg-blue-100 dark:bg-blue-950/50',
          label: 'Processing',
          clickable: true
        };
      case 'processed_pending_approval': 
        return { 
          icon: <UserCheck className="h-5 w-5" />, 
          color: 'text-orange-600 dark:text-orange-400', 
          bg: isSelected ? 'bg-muted/80 border-orange-500 dark:border-orange-600' : 'bg-card border-orange-200 dark:border-orange-800',
          iconBg: 'bg-orange-100 dark:bg-orange-950/50',
          label: 'Pending Approval',
          clickable: true
        };
      case 'approved': 
        return { 
          icon: <CheckSquare className="h-5 w-5" />, 
          color: 'text-green-600 dark:text-green-400', 
          bg: isSelected ? 'bg-muted/80 border-green-500 dark:border-green-600' : 'bg-card border-green-200 dark:border-green-800',
          iconBg: 'bg-green-100 dark:bg-green-950/50',
          label: 'Approved',
          clickable: true
        };
      case 'uploading_to_magento': 
        return { 
          icon: <TrendingUp className="h-5 w-5" />, 
          color: 'text-purple-600 dark:text-purple-400', 
          bg: isSelected ? 'bg-muted/80 border-purple-500 dark:border-purple-600' : 'bg-card border-purple-200 dark:border-purple-800',
          iconBg: 'bg-purple-100 dark:bg-purple-950/50',
          label: 'Uploading',
          clickable: true
        };
      case 'complete': 
        return { 
          icon: <CheckCircle className="h-5 w-5" />, 
          color: 'text-emerald-600 dark:text-emerald-400', 
          bg: isSelected ? 'bg-muted/80 border-emerald-500 dark:border-emerald-600' : 'bg-card border-emerald-200 dark:border-emerald-800',
          iconBg: 'bg-emerald-100 dark:bg-emerald-950/50',
          label: 'Complete',
          clickable: true
        };
      case 'failed': 
        return { 
          icon: <XCircle className="h-5 w-5" />, 
          color: 'text-red-600 dark:text-red-400', 
          bg: isSelected ? 'bg-muted/80 border-red-500 dark:border-red-600' : 'bg-card border-red-200 dark:border-red-800',
          iconBg: 'bg-red-100 dark:bg-red-950/50',
          label: 'Failed',
          clickable: true
        };
      case 'rejected': 
        return { 
          icon: <XCircle className="h-5 w-5" />, 
          color: 'text-muted-foreground', 
          bg: isSelected ? 'bg-muted/80 border-muted-foreground' : 'bg-muted/50 border-border',
          iconBg: 'bg-muted/70',
          label: 'Rejected',
          clickable: true
        };
      default: 
        return { 
          icon: <Activity className="h-5 w-5" />, 
          color: 'text-muted-foreground', 
          bg: 'bg-muted/50 border-border',
          iconBg: 'bg-muted/70',
          label: status.replace(/_/g, ' '),
          clickable: true
        };
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
            Jobs Dashboard
          </h1>
          <p className="text-lg text-muted-foreground mt-2">Manage and monitor your image processing jobs</p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            onClick={handleViewPendingApprovals}
            size="lg"
            variant="outline"
            disabled={!hasPermission('jobs', 'approve')}
            className="flex items-center gap-3 border-2 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:border-orange-400 dark:hover:border-orange-600 shadow-lg hover:shadow-xl transition-all duration-300"
            title={!hasPermission('jobs', 'approve') ? "You don't have permission to approve jobs" : undefined}
          >
            <UserCheck className="h-5 w-5" />
            View Pending Approvals
            {jobStats.processed_pending_approval > 0 && (
              <Badge className="ml-2 bg-orange-500 dark:bg-orange-600 text-white">
                {jobStats.processed_pending_approval}
              </Badge>
            )}
          </Button>
          <Button
            onClick={handleCreateJobs}
            size="lg"
            disabled={!hasPermission('jobs', 'create')}
            className="flex items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300"
            title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
          >
            <Plus className="h-5 w-5" />
            {!hasPermission('jobs', 'create') ? 'No Permission' : 'Create New Jobs'}
            {selection.selectedIds.size > 0 && (
              <Badge variant="secondary" className="ml-2 bg-white text-blue-700">
                {selection.selectedIds.size}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Active Async Job Alert */}
      {activeAsyncJob && (
        <JobProcessingBanner
          jobCount={activeAsyncJob.productCount}
          jobType="creation"
          onMonitor={() => handleMonitorAsyncJob(activeAsyncJob.id)}
        />
      )}


      {/* Job Statistics Overview - Business Critical Only */}
      <Card className="border-2 border-border bg-gradient-to-r from-muted/20 to-muted/30">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-2xl">
            <div className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-muted-foreground" />
              Business Operations Monitor
            </div>
            {selectedStatusFilter && (
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedStatusFilter(null)}
              >
                Filtered by: {getStatCardConfig(selectedStatusFilter).label} ✕
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Clean 2x3 Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Jobs - First position */}
            {(() => {
              const status = 'total';
              const count = jobStats[status];
              const config = getStatCardConfig(status);
              
              return (
                <Card 
                  key={status} 
                  className={`border-2 ${config.bg}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-2 rounded-lg ${config.iconBg}`}>
                        <span className={config.color}>
                          {config.icon}
                        </span>
                      </div>
                      <span className={`text-2xl font-bold ${config.color}`}>
                        {loadingStats ? '-' : count}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {config.label}
                    </p>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Business Critical Statuses */}
            {(['processed_pending_approval', 'uploading_to_magento', 'complete', 'failed', 'rejected'] as const).map((status) => {
              const count = jobStats[status];
              const config = getStatCardConfig(status);
              
              return (
                <Card 
                  key={status} 
                  className={`border-2 transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-105 ${config.bg}`}
                  onClick={() => handleStatusFilter(status)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-2 rounded-lg ${config.iconBg}`}>
                        <span className={config.color}>
                          {config.icon}
                        </span>
                      </div>
                      <span className={`text-2xl font-bold ${config.color}`}>
                        {loadingStats ? '-' : count}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {config.label}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Click to filter
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Status Information Alert */}
          {(jobStats.uploading_to_magento > 0 || jobStats.failed > 0) && (
            <WarningBanner
              variant={jobStats.failed > 0 ? "warning" : "info"}
              title="📊 Status Update"
              message={`${jobStats.uploading_to_magento > 0 ? `${jobStats.uploading_to_magento} jobs are currently uploading to your live site.` : ''} ${jobStats.failed > 0 ? `${jobStats.failed} jobs have failed and may need attention.` : ''}`.trim()}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Compact Job History */}
      {jobHistory.length > 0 && (
        <div className="bg-muted/50 rounded-lg p-3 border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Recent Activity</span>
              <Badge variant="secondary" className="text-xs">
                {jobHistory.length}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsJobHistoryExpanded(!isJobHistoryExpanded)}
              className="h-6 px-2 text-muted-foreground hover:text-foreground"
            >
              {isJobHistoryExpanded ? 'Hide' : 'Show'}
            </Button>
          </div>
          
          {isJobHistoryExpanded && (
            <div className="space-y-2">
              {jobHistory.slice(0, 3).map((job) => (
                <div key={job.id} className="flex items-center justify-between text-xs bg-background rounded p-2 border">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-foreground">{job.storeName}</span>
                    <span className="text-muted-foreground">{job.productCount} products</span>
                    <Badge variant={job.status === 'completed' ? 'default' : 'secondary'} className="text-xs px-1 py-0">
                      {job.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">
                      {new Date(job.createdAt).toLocaleDateString()}
                    </span>
                    {job.status === 'creating' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMonitorAsyncJob(job.id)}
                        className="h-6 px-2 text-xs"
                      >
                        Monitor
                      </Button>
                    )}
                    {job.batchId && job.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/jobs/monitor?batch_id=${job.batchId}`)}
                        className="h-6 px-2 text-xs"
                      >
                        View Batch
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Jobs List */}
      <Card className="border border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Package2 className="h-5 w-5 text-muted-foreground" />
              All Jobs
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={autoRefresh ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : ''}
              >
                {autoRefresh ? <Pause className="w-4 h-4 mr-1" /> : <PlayCircle className="w-4 h-4 mr-1" />}
                {autoRefresh ? 'Auto' : 'Manual'}
              </Button>
              <Button variant="outline" onClick={() => {
                loadJobStats();
                setRefreshKey(prev => prev + 1);
              }}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {autoRefresh && (
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 ${isPolling ? 'bg-green-500' : 'bg-gray-400'} rounded-full ${isPolling ? 'animate-pulse' : ''}`}></div>
                {isPolling ? `Monitoring ${activeJobs.length} active jobs` : 'No active jobs to monitor'}
              </div>
              <div>
                Last updated: {new Date(lastRefresh).toLocaleTimeString()}
              </div>
            </div>
          )}
          <JobsList
            key={refreshKey}
            statusFilter={selectedStatusFilter || undefined}
            onJobSelect={handleJobSelect}
            onJobStatusUpdate={handleJobUpdate}
          />
        </CardContent>
      </Card>
    </div>
  );
}